import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/ScrollToTop';
import MainLayout from './layouts/MainLayout';
import VerificationModal from './components/VerificationModal';
import CheckoutModal from './components/CheckoutModal';
import Home from './pages/Home';
import Shop from './pages/Shop';
import AboutUs from './pages/AboutUs';
import NewsPromotions from './pages/NewsPromotions';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import { PRODUCTS } from './constants';
import { Product, OrderItem, AdminUser } from './types';
import { onAuthStateChanged, getCurrentAdmin, getCurrentRetailer, signOutRetailer, RetailerUser } from './services/authService';

// Lazy-loaded routes (Step D: code splitting)
const RetailerApplicationForm = lazy(() => import('./components/RetailerApplicationForm'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AdminLoginForm = lazy(() => import('./components/admin/AdminLoginForm'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-brand-warm">
    <div className="text-brand-dark font-medium">Loading...</div>
  </div>
);

// Admin route component with its own auth check
const AdminRoute: React.FC<{
  adminUser: AdminUser | null;
  setAdminUser: (admin: AdminUser | null) => void;
}> = ({ adminUser, setAdminUser }) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(!adminUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (adminUser) {
      setIsCheckingAuth(false);
      return;
    }
    setIsCheckingAuth(true);
    const unsubscribe = onAuthStateChanged(async (user) => {
      if (user) {
        const admin = await getCurrentAdmin(user);
        if (admin) {
          setAdminUser(admin);
        }
      }
      setIsCheckingAuth(false);
    });
    return () => unsubscribe();
  }, [adminUser, setAdminUser]);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (adminUser) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AdminDashboard
          admin={adminUser}
          onLogout={() => { setAdminUser(null); navigate('/'); }}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <AdminLoginForm
        onLoginSuccess={(admin) => setAdminUser(admin)}
      />
    </Suspense>
  );
};

// Inner app component (needs to be inside BrowserRouter to use useNavigate)
const AppInner: React.FC = () => {
  const [cartItems, setCartItems] = useState<OrderItem[]>([]);
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [retailerUser, setRetailerUser] = useState<RetailerUser | null>(null);

  const [products] = useState<Product[]>(PRODUCTS);
  const navigate = useNavigate();

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Check for existing retailer session on app load
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (user) => {
      if (user) {
        const retailer = await getCurrentRetailer(user);
        if (retailer) {
          setRetailerUser(retailer);
          setIsVerified(true);
        }
      } else {
        setRetailerUser(null);
        setIsVerified(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // ─── Cart Logic ───

  const handleAddToCart = useCallback((product: Product, quantity: number) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.map(item =>
          item.productId === product.id
            ? { ...item, quantity: Math.min(item.quantity + quantity, product.stock) }
            : item
        );
      }
      return [...prev, {
        productId: product.id,
        sku: product.sku,
        name: product.name,
        quantity,
        price: product.price
      }];
    });
  }, []);

  const handleUpdateQuantity = useCallback((productId: string, quantity: number) => {
    setCartItems(prev =>
      prev.map(item =>
        item.productId === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const handleRemoveItem = useCallback((productId: string) => {
    setCartItems(prev => prev.filter(item => item.productId !== productId));
  }, []);

  const handleClearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // ─── Auth Handlers ───

  const handleRetailerLogout = useCallback(async () => {
    await signOutRetailer();
    setRetailerUser(null);
    setIsVerified(false);
    setCartItems([]);
  }, []);

  const handleLoginSuccess = useCallback((retailer: RetailerUser) => {
    setRetailerUser(retailer);
    setIsVerified(true);
    setIsVerificationOpen(false);
  }, []);

  const handleCheckoutRequest = () => {
    if (!isVerified) {
      setIsVerificationOpen(true);
    } else {
      setIsCheckoutOpen(true);
    }
  };

  const handleLoginClick = () => {
    if (isVerified && retailerUser) {
      navigate('/shop');
    } else {
      setIsVerificationOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-brand-warm font-sans text-brand-dark">
      <ScrollToTop />
      <Routes>
        {/* Public routes with shared layout (Nav + Footer) */}
        <Route element={
          <MainLayout
            cartCount={cartCount}
            onCartClick={handleCheckoutRequest}
            onLoginClick={handleLoginClick}
            retailerUser={retailerUser}
            onLogout={handleRetailerLogout}
          />
        }>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={
            <Shop
              products={products}
              isVerified={isVerified}
              onAddToCart={handleAddToCart}
              cartItems={cartItems}
            />
          } />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/news" element={<NewsPromotions />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        {/* Routes WITHOUT shared layout */}
        <Route path="/apply" element={
          <Suspense fallback={<LoadingFallback />}>
            <RetailerApplicationForm />
          </Suspense>
        } />
        <Route path="/admin" element={
          <AdminRoute adminUser={adminUser} setAdminUser={setAdminUser} />
        } />

        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Modals stay as overlays */}
      <VerificationModal
        isOpen={isVerificationOpen}
        onClose={() => setIsVerificationOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {retailerUser && (
        <CheckoutModal
          isOpen={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
          cartItems={cartItems}
          products={products}
          retailerUser={retailerUser}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onClearCart={handleClearCart}
        />
      )}
    </div>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
